import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, Plus, MessageSquare, ShieldCheck, User, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const CATEGORIES = [
  { k: "bug", label: "Bug" },
  { k: "how_to", label: "How-to / help" },
  { k: "billing", label: "Billing" },
  { k: "feature_request", label: "Feature request" },
  { k: "data_issue", label: "Data issue" },
  { k: "other", label: "Other" },
];
const PRIORITIES = [
  { k: "low", label: "Low" },
  { k: "normal", label: "Normal" },
  { k: "high", label: "High" },
  { k: "urgent", label: "Urgent" },
];

const STATUS_TONE = {
  open: "bg-[#F79009]/10 text-[#F79009]",
  in_progress: "bg-[#457b9d]/10 text-[#457b9d]",
  waiting_on_garage: "bg-[#e63946]/10 text-[#e63946]",
  resolved: "bg-[#12B76A]/10 text-[#12B76A]",
  closed: "bg-[#66708515] text-[#667085]",
};

export default function SupportCasesPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", category: "other", priority: "normal" });
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/support/cases")
      .then((r) => setCases(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submitNew = async () => {
    if (!form.subject.trim() || !form.description.trim()) { toast.error("Subject and description required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/support/cases", form);
      toast.success(`Case ${r.data.case_number} opened`);
      setNewOpen(false);
      setForm({ subject: "", description: "", category: "other", priority: "normal" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const openDetail = async (c) => {
    try {
      const r = await api.get(`/support/cases/${c.id}`);
      setSelected(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      const r = await api.post(`/support/cases/${selected.id}/comment`, { text: reply.trim() });
      setSelected(r.data);
      setReply("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="support-cases-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <LifeBuoy size={22} className="text-[#457b9d]" /> Support
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Get help from the SparesHub team. Bugs, how-to, billing — we typically reply within a working day.
          </p>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="support-new-case-btn"
              className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
            >
              <Plus size={14} className="mr-1.5" /> New case
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#1d3557] font-semibold">Report an issue</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Subject</Label>
                <Input
                  data-testid="support-new-subject"
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="mt-1 rounded-md"
                  placeholder="One-line summary"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Description</Label>
                <Textarea
                  data-testid="support-new-description"
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="mt-1 rounded-md"
                  placeholder="What went wrong? Include steps to reproduce, browser, and screenshots if helpful."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Category</Label>
                  <select
                    data-testid="support-new-category"
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                  >
                    {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Priority</Label>
                  <select
                    data-testid="support-new-priority"
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                    className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                  >
                    {PRIORITIES.map((p) => <option key={p.k} value={p.k}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <Button
                onClick={submitNew}
                disabled={saving}
                data-testid="support-new-submit"
                className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
              >
                {saving ? "Sending…" : "Submit case"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* List + Detail split */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="support-cases-list">
          {loading && <div className="p-6 text-[13px] text-[#667085]">Loading…</div>}
          {!loading && cases.length === 0 && (
            <div className="p-8 text-center text-[13px] text-[#667085]">
              No cases yet. Click <strong>New case</strong> to raise your first support ticket.
            </div>
          )}
          {cases.map((c) => (
            <button
              key={c.id}
              onClick={() => openDetail(c)}
              data-testid={`support-case-item-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#F1FAEE] hover:bg-[#F7F9FC] transition-colors ${
                selected?.id === c.id ? "bg-[#F1FAEE]" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-mono text-[#457b9d] font-semibold">{c.case_number}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[c.status] || STATUS_TONE.open}`}>
                  {c.status?.replace(/_/g, " ")}
                </span>
              </div>
              <div className="text-[13px] text-[#1d3557] font-semibold mt-1 truncate">{c.subject}</div>
              <div className="text-[11px] text-[#667085] font-mono mt-1 flex items-center gap-1">
                <Clock size={10} /> {fmtDateTime(c.updated_at)}
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 bg-white border border-[#E4E7EC] rounded-lg" data-testid="support-case-detail">
          {!selected ? (
            <div className="p-8 text-center text-[13px] text-[#667085]">
              Select a case to view the conversation.
            </div>
          ) : (
            <div className="p-5">
              <div className="text-[11px] font-mono text-[#457b9d] font-semibold">{selected.case_number}</div>
              <h2 className="text-[18px] font-bold text-[#1d3557] mt-1" data-testid="support-case-detail-subject">{selected.subject}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[selected.status] || STATUS_TONE.open}`}>
                  {selected.status?.replace(/_/g, " ")}
                </span>
                <span className="text-[11px] text-[#667085] font-mono">Opened {fmtDateTime(selected.created_at)}</span>
              </div>
              <div className="mt-4 text-[13px] text-[#1d3557] whitespace-pre-wrap bg-[#F7F9FC] rounded-md p-3 border border-[#E4E7EC]">
                {selected.description}
              </div>

              <div className="mt-5">
                <h3 className="text-[13px] font-semibold text-[#1d3557] flex items-center gap-1.5">
                  <MessageSquare size={13} className="text-[#457b9d]" /> Conversation
                </h3>
                <div className="mt-2 space-y-3">
                  {(selected.updates || []).filter((u) => u.kind !== "internal").length === 0 ? (
                    <div className="text-[12px] text-[#667085] py-2">No replies yet — the SparesHub team will respond soon.</div>
                  ) : (
                    selected.updates.filter((u) => u.kind !== "internal").map((u, i) => (
                      <div key={i} className={`rounded-md p-3 border ${
                        u.by_role === "admin" ? "border-[#457b9d]/40 bg-[#457b9d]/5" : "border-[#E4E7EC] bg-white"
                      }`}>
                        <div className="text-[11px] font-semibold text-[#1d3557] flex items-center gap-1.5">
                          {u.by_role === "admin" ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#457b9d]">
                              <ShieldCheck size={10} /> SparesHub
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1d3557]">
                              <User size={10} /> You
                            </span>
                          )}
                          · {u.by_name || u.by_email} · {fmtDateTime(u.at)}
                        </div>
                        <div className="mt-1.5 text-[13px] text-[#1d3557] whitespace-pre-wrap">{u.text}</div>
                      </div>
                    ))
                  )}
                </div>
                {selected.status !== "closed" && (
                  <div className="mt-4">
                    <Textarea
                      rows={3}
                      data-testid="support-reply-input"
                      placeholder="Type a reply…"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      className="rounded-md"
                    />
                    <Button
                      onClick={sendReply}
                      disabled={!reply.trim() || saving}
                      data-testid="support-reply-send"
                      className="mt-2 h-9 bg-[#1d3557] hover:bg-[#152941] rounded-md text-white font-semibold text-[13px] px-3"
                    >
                      Send reply
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
